import { useState } from "react";
import gql from "graphql-tag";
import { useQuery, useMutation } from "@apollo/client";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import TextField from "../components/form/TextField";
import EditUserEmailPreferences from "../components/EditUserEmailPreferences";
import EditUserUnits from "../components/EditUserUnits";
import { useAuth } from "../AuthContext";
import { Form, Field } from "react-final-form";

import DefaultLayout from "../components/DefaultLayout";

const ME_QUERY = gql`
  query {
    me {
      who {
        id
        ... on User {
          name
          email
          useMetricUnits
          emailPreferences {
            eventButtonIncidents
            newNearMissIsRecorded
          }
        }
      }
    }
  }
`;
const UPDATE_USER_LOGIN_EMAIL = gql`
  mutation UserChangeEmail($input: ChangeEmailInput!) {
    me {
      changeEmail(input: $input) {
        error {
          code
          message
        }
        user {
          id
          email
        }
      }
    }
  }
`;

const validateEmail = (value) => {
  if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
    return "Please enter a valid email address";
};

const ChangeEmailDialog = ({ open, onClose }) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [updateEmail] = useMutation(UPDATE_USER_LOGIN_EMAIL);

  const handleSubmit = (values, formApi) => {
    setErrorMessage(null);
    return updateEmail({
      variables: { input: { newEmail: values.newEmail } },
    }).then(
      ({ data }) => {
        const result = data?.me?.changeEmail;
        if (result?.error) {
          const message =
            result.error.code === 200311 || result.error.code === 200314
              ? "This email address is already in use."
              : result.error.message;
          setErrorMessage(message);
        } else {
          formApi.reset();
          onClose(`Email has been changed to ${result.user.email}`);
        }
      },
      (err) => {
        setErrorMessage(err.message);
      },
    );
  };

  const handleClose = () => {
    setErrorMessage(null);
    onClose(null);
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{ newEmail: "" }}
      render={({ handleSubmit, submitting, form }) => (
        <Dialog
          open={open}
          onClose={() => {
            form.reset();
            handleClose();
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Change Email Address</DialogTitle>
          <DialogContent>
            <Field
              component={TextField}
              name="newEmail"
              margin="normal"
              autoComplete="off"
              label="New email address"
              fullWidth
              autoFocus
              validate={validateEmail}
            />
            {errorMessage && (
              <Typography color="error" style={{ marginTop: 8 }}>
                {errorMessage}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={submitting} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              disabled={submitting}
              onClick={handleSubmit}
              color="primary"
            >
              Update Email
            </Button>
          </DialogActions>
        </Dialog>
      )}
    />
  );
};

const AccountPage = ({ nearMissEnabled }) => {
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const { account } = useAuth();
  const { loading, error, data, refetch } = useQuery(ME_QUERY);

  if (loading) return <DefaultLayout>Loading...</DefaultLayout>;
  if (error) return <DefaultLayout>{JSON.stringify(error)}</DefaultLayout>;

  const {
    me: { who },
  } = data;

  return (
    <DefaultLayout>
      <Typography variant="h5" gutterBottom>
        Account
      </Typography>
      <p>Name: {who.name}</p>
      <p>ID: {who.id}</p>
      <p>
        Email: {who.email}{" "}
        <Button
          sx={{ ml: 2 }}
          size="small"
          variant="outlined"
          onClick={() => setChangeEmailOpen(true)}
        >
          Change email address
        </Button>
      </p>
      <ChangeEmailDialog
        open={changeEmailOpen}
        onClose={(message) => {
          setChangeEmailOpen(false);
          if (message) {
            setSnackbarMessage(message);
            refetch();
          }
        }}
        userId={who.id}
      />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
      <Typography variant="h6" gutterBottom>
        Units
      </Typography>
      <EditUserUnits
        userId={who.id}
        useMetricUnits={who.useMetricUnits}
        tenantUseMetricUnits={account?.tenant?.useMetricUnits}
      />
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Email notification preferences
      </Typography>
      <EditUserEmailPreferences
        userId={who.id}
        emailPreferences={who.emailPreferences}
        nearMissEnabled={nearMissEnabled}
      />
    </DefaultLayout>
  );
};

export default AccountPage;
